import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { ApiUrlService } from '../../shared/services/api-url.service';
import { ApiService, ApiRoute } from '../../shared/constants/api-services.constant';
import {
  AssetConfigResponse,
  AssetSearchResponse,
  DataMaintenanceAsset,
  DataMaintenanceAssetResponse,
  DataTableContent,
  DevOpsCommitResponse,
  DevOpsUncommitedCountResponse,
  FileUpload,
  ResetWorkspaceResponse,
  WorktreeDeleteCheckResponse,
  WorktreeListResponseDTO,
} from './data-maintenance.model';

export interface DevOpsTokenResponse {
  accessToken: string;
  expiresOn: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataMaintenanceService {
  private apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly apiUrlService: ApiUrlService
  ) {
    this.apiUrl = this.apiUrlService.build(ApiService.DataMaintenance);
  }

  getConfig(accessToken: string): Observable<AssetConfigResponse> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.get<AssetConfigResponse>(`${this.apiUrl}/config`, { headers });
  }

  getAllAssets(
    folderPath: string,
    repositoryKey: string,
    sortKey?: string,
    sortOrder?: string,
    page?: number,
    pageSize?: number,
    nameContains?: string,
    fileTypeContains?: string,
    uploadedByContains?: string,
    searchTerm?: string
  ): Observable<DataMaintenanceAssetResponse> {
    let params = new HttpParams();

    params = params.set('repositoryKey', repositoryKey);

    if (folderPath) {
      params = params.set('directoryPath', encodeURIComponent(folderPath));
    }
    if (sortKey) {
      params = params.set('sortBy', sortKey);
    }
    if (sortOrder) {
      params = params.set('sortDir', sortOrder);
    }
    if (page !== undefined) {
      params = params.set('page', page.toString());
    }
    if (pageSize !== undefined) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (nameContains !== undefined) {
      params = params.set('nameContains', nameContains);
    }
    if (fileTypeContains !== undefined) {
      params = params.set('fileTypeContains', fileTypeContains);
    }
    if (uploadedByContains !== undefined) {
      params = params.set('uploadedByContains', uploadedByContains);
    }

    return this.http.get<DataMaintenanceAssetResponse>(`${this.apiUrl}/${ApiRoute.Assets}`, { params });
  }

  getAsset(assetPath: string, repositoryKey: string): Observable<DataMaintenanceAsset> {
    const params = new HttpParams().set('repositoryKey', repositoryKey);
    return this.http.get<DataMaintenanceAsset>(`${this.apiUrl}/${ApiRoute.Assets}/${assetPath}`, { params });
  }

  downloadAsset(assetPath: string, repositoryKey: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('download', 'true').set('repositoryKey', repositoryKey);
    return this.http.get(`${this.apiUrl}/${ApiRoute.Assets}/${encodeURIComponent(assetPath)}`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  injectUserAsset(assetPath: string, asset: DataMaintenanceAsset): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${ApiRoute.UserAssets}/${assetPath}`, asset);
  }

  renameUserAsset(
    assetPath: string,
    newFileName: string,
    isUserOverride: boolean,
    categoryId: string,
    repositoryKey: string,
    accessToken: string
  ): Observable<void> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.patch<void>(
      `${this.apiUrl}/${ApiRoute.UserAssets}/rename`,
      { assetPath, newFileName, isUserOverride, categoryId, repositoryKey },
      { headers }
    );
  }
  isDataTableContent<TKeys extends string = string>(obj: unknown): obj is DataTableContent<TKeys> {
    return typeof obj === 'object' && obj !== null && 'tableName' in obj && 'data' in obj && Array.isArray((obj as any).data);
  }

  exchangeAuthCode(authCode: string, codeVerifier: string, redirectUri: string): Observable<{ sessionId: string }> {
    return this.http.post<{ sessionId: string }>(`${this.apiUrl}/auth/azuread/exchange`, {
      authCode,
      codeVerifier,
      redirectUri,
    });
  }

  commitUserAssets(accessToken: string, summary: string): Observable<DevOpsCommitResponse> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.post<DevOpsCommitResponse>(`${this.apiUrl}/devops/assets/commit`, { summary }, { headers });
  }

  getUncommitedAssetCount(accessToken: string): Observable<DevOpsUncommitedCountResponse> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.get<DevOpsUncommitedCountResponse>(`${this.apiUrl}/devops/assets/uncommitted-count`, { headers });
  }

  getWorktrees(): Observable<WorktreeListResponseDTO> {
    return this.http.get<WorktreeListResponseDTO>(`${this.apiUrl}/${ApiRoute.Worktrees}`);
  }

  setActiveWorktree(worktree: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${ApiRoute.Worktrees}/active`, { worktree });
  }

  createWorktree(worktree: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${ApiRoute.Worktrees}`, { worktree });
  }

  copyWorkTree(sourceWorktree: string, destinationWorktree: string, repositoryKey: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${ApiRoute.Worktrees}/copy`, {
      sourceWorktree,
      destinationWorktree,
      repositoryKey,
    });
  }

  deleteWorktree(accessToken: string, worktree: string): Observable<void> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.delete<void>(`${this.apiUrl}/${ApiRoute.Worktrees}/${worktree}`, { headers });
  }

  checkWorktreeDelete(worktree: string, accessToken: string): Observable<WorktreeDeleteCheckResponse> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    const params = new HttpParams().set('checkonly', 'true');
    return this.http.delete<WorktreeDeleteCheckResponse>(`${this.apiUrl}/${ApiRoute.Worktrees}/${worktree}`, { headers, params });
  }

  fileUploadOrReplace(fileRequest: FileUpload, accessToken: string): Observable<void> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    const formData = new FormData();
    formData.append('file', fileRequest.file, fileRequest.file.name);
    formData.append('categoryId', fileRequest.categoryId);
    formData.append('productName', fileRequest.productName);
    formData.append('repositoryKey', fileRequest.repositoryKey);
    if (fileRequest.targetFileName) {
      formData.append('targetFileName', fileRequest.targetFileName);
      return this.http.put<void>(`${this.apiUrl}/${ApiRoute.UserAssets}`, formData, { headers });
    }
    return this.http.post<void>(`${this.apiUrl}/${ApiRoute.UserAssets}`, formData, { headers });
  }

  deleteUserAsset(assetPath: string, categoryId: string, repositoryKey: string, accessToken: string): Observable<void> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.post<void>(
      `${this.apiUrl}/${ApiRoute.UserAssets}/delete`,
      { assetPath, categoryId, repositoryKey },
      { headers }
    );
  }

  resetAsset(accessToken: string, assetPath: string): Observable<void> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.post<void>(`${this.apiUrl}/${ApiRoute.Assets}/reset`, { assetPath }, { headers });
  }

  resetWorkspace(accessToken: string): Observable<ResetWorkspaceResponse> {
    const headers = { DevopsAuthorization: `Bearer ${accessToken}` };
    return this.http.post<ResetWorkspaceResponse>(`${this.apiUrl}/workspace/reset`, {}, { headers });
  }

  searchAssets(searchTerm: string, page: number, pageSize: number, repositoryKey: string): Observable<AssetSearchResponse> {
    let params = new HttpParams().set('searchTerm', searchTerm).set('page', page.toString()).set('pageSize', pageSize.toString());
    params = params.set('repositoryKey', repositoryKey);
    return this.http.get<AssetSearchResponse>(`${this.apiUrl}/assets/search`, { params });
  }
}
